<template>
  <div id="checkout">
    <section v-show="!loading">
      <div class="payment-info">
        <div id="card-element" class="field"></div>
      </div>

      <div class="payment-button-wrapper">
        <v-btn
          @click="processPaymentSubscribe"
          color="success"
          class="payment-button"
          ref="submitButton"
          :disabled="payButtonDisabled || !this.payButtonEnabled"
          >Match-Abo jetzt kaufen</v-btn
        >
        <div class="payment-button__pulse"></div>
      </div>
    </section>
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="green"
    ></v-progress-circular>

    <div id="card-errors" ref="paymentErrors" class="element-errors hide">
      {{ stripeValidationError }}
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import stripeMixin from '../../mixins/stripe';

export default {
  name: 'stripe-card',

  props: {
    payButtonEnabled: {
      type: Boolean,
      default: false,
    },
    orderedItem: {
      type: Object,
      default: () => {},
    },
    customerData: {
      type: Object,
      default: () => {},
    },
    latestSubscription: {
      type: Object,
      default: () => {},
    },
    promoCode: {
      type: Object,
      default: () => {},
    },
    billingData: {
      type: Object,
      default: () => {},
    },
    stripeCustomerId: {
      type: String,
      default: () => null,
    },
  },

  mixins: [stripeMixin],

  data() {
    return {
      payButtonDisabled: false,
      cardElement: null,
    };
  },

  computed: {
    ...mapState(['user_data', 'userType']),
  },

  methods: {
    createCardElement() {
      // Create an instance of Elements.
      const elements = this.stripe.elements();

      // Prepare the styles for Elements.
      const style = {
        base: {
          iconColor: '#666ee8',
          color: '#31325f',
          fontWeight: 400,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '15px',
          '::placeholder': {
            color: '#aab7c4',
          },
          ':-webkit-autofill': {
            color: '#666ee8',
          },
        },
      };

      // Create a Card Element and pass some custom styles to it.
      this.cardElement = elements.create('card', { style, hidePostalCode: true });

      // Mount the Card Element on the page.
      this.cardElement.mount('#card-element');

      // Monitor change events on the Card Element to display any errors.
      this.cardElement.on('change', ({ error }) => {
        console.log('card element changed', error);
        if (error) {
          this.payButtonDisabled = false;
          this.stripeValidationError = error.message;
          this.$refs.paymentErrors.classList.remove('hide');
        } else {
          this.$refs.paymentErrors.classList.add('hide');
        }
        // Re-enable the Pay button.
        this.payButtonDisabled = false;
      });
    },

    createPaymentMethod({ isPaymentRetry, invoiceId }) {
      console.log('createPaymentMethod', isPaymentRetry, invoiceId);
      // Set up payment method for recurring usage
      this.stripe
        .createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.customerData.name,
            email: this.customerData.email,
            address: this.billingData.address,
          },
        })
        .then((result) => {
          if (result.error) {
            this.payButtonDisabled = false;
            this.stripeValidationError = result.error.message;
            this.$refs.paymentErrors.classList.remove('hide');
            this.$store.state.loading = false;
          } else if (isPaymentRetry) {
            // Update the payment method and retry invoice payment
            this.retryInvoiceWithNewPaymentMethod({
              paymentMethodId: result.paymentMethod.id,
              invoiceId,
            });
          } else {
            // Create the subscription
            this.createSubscription({
              paymentMethodId: result.paymentMethod.id,
              promoCode: this.promoCode,
            });
          }
        });
    },

    handlePaymentThatRequiresCustomerAction({
      subscription,
      invoice,
      priceId,
      paymentMethodId,
      isRetry,
    }) {
      console.log('handlePaymentThatRequiresCustomerAction', subscription, invoice);

      if (subscription && subscription.status === 'active') {
        // subscription is active, no customer actions required.
        return { subscription, priceId, paymentMethodId };
      }

      // If it's a first payment attempt, the payment intent is on the subscription latest invoice.
      // If it's a retry, the payment intent will be on the invoice itself.
      const paymentIntent = invoice
        ? invoice.payment_intent
        : subscription.latest_invoice.payment_intent;

      if (
        paymentIntent.status === 'requires_action' ||
        (isRetry === true && paymentIntent.status === 'requires_payment_method')
      ) {
        return this.stripe
          .confirmCardPayment(paymentIntent.client_secret, {
            payment_method: paymentMethodId,
          })
          .then((result) => {
            if (result.error) {
              // start code flow to handle updating the payment details
              // Display error message in your UI.
              // The card was declined (i.e. insufficient funds, card has expired, etc)
              throw result;
            } else if (result.paymentIntent.status === 'succeeded') {
              // There's a risk of the customer closing the window before callback
              // execution. To handle this case, set up a webhook endpoint and
              // listen to invoice.paid. This webhook endpoint returns an Invoice.
              return {
                priceId,
                subscription,
                invoice,
                paymentMethodId,
                repeatedPaymentStatus: 'succeeded',
              };
            }
          });
      }
      // No customer action needed
      return { subscription, priceId, paymentMethodId };
    },
  },

  async mounted() {
    this.payButtonDisabled = true;
    this.loading = true;

    this.createCardElement();

    this.payButtonDisabled = false;
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';
@import '@/styles/blue-ring.scss';

.payment-info {
  margin: 15px auto;
  /* width: 90%; */
  padding: 10px 20px;
  border-radius: 8px;
  box-shadow: 2px 3px 3px 2px rgba(0, 0, 0, 0.25);
}

.payment-button-wrapper {
  margin: 15px auto;
  width: 90%;
  position: relative;
  text-align: center;
}

.payment-button {
  // margin: 15px auto;
  width: 100%;
  padding: 8px;
  border-radius: 15px;
  @include mainLinearGradient;
  color: white;
  margin: 0 !important;
  font-weight: bold;

  &__pulse {
    @include pulse3;
    position: absolute;
    top: -1px;
    height: 40px;
    width: 100%;
    border-radius: 15px;
    pointer-events: none; // pass through click event
  }
}

.hide {
  display: none;
}
</style>
